import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { LucideIcon } from 'lucide-react'
import { useCountUp } from '@/hooks/useCountUp'

type StatusLevel = 'normal' | 'success' | 'warning' | 'critical' | 'voice'

interface LiveStatusCardProps {
  title: string
  value: number | string
  icon: LucideIcon
  status?: StatusLevel
  subtitle?: string
  pulse?: boolean
  loading?: boolean
  href?: string
}

const statusConfig: Record<StatusLevel, { border: string; bg: string; icon: string; pulse: string }> = {
  normal: {
    border: 'border-brand-primary/30',
    bg: 'bg-brand-primary/5',
    icon: 'text-brand-primary',
    pulse: 'shadow-brand-primary/20'
  },
  success: {
    border: 'border-emerald-500/30',
    bg: 'bg-emerald-500/5',
    icon: 'text-emerald-500',
    pulse: 'shadow-emerald-500/20'
  },
  warning: {
    border: 'border-amber-500/30',
    bg: 'bg-amber-500/5',
    icon: 'text-amber-500',
    pulse: 'shadow-amber-500/20'
  },
  critical: {
    border: 'border-red-500/30',
    bg: 'bg-red-500/5',
    icon: 'text-red-500',
    pulse: 'shadow-red-500/20'
  },
  voice: {
    border: 'border-violet-500/30',
    bg: 'bg-violet-500/5',
    icon: 'text-violet-500',
    pulse: 'shadow-violet-500/20'
  }
}

export default function LiveStatusCard({
  title,
  value,
  icon: Icon,
  status = 'normal',
  subtitle,
  pulse = false,
  loading = false,
  href
}: LiveStatusCardProps) {
  const navigate = useNavigate()
  const config = statusConfig[status]
  const animatedValue = useCountUp(typeof value === 'number' ? value : 0)

  if (loading) {
    return (
      <Card className="border-2 border-muted">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-full bg-muted animate-pulse" />
            <div className="flex-1 space-y-2">
              <div className="h-4 w-20 bg-muted animate-pulse rounded" />
              <div className="h-6 w-12 bg-muted animate-pulse rounded" />
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card
      className={cn(
        // Targeted transitions replace the generic `transition-colors`.
        // Hover feedback gated behind `(hover: hover) and (pointer: fine)`
        // so touch devices don't fire false hover on tap.
        'border-2 relative overflow-hidden',
        '[transition:transform_160ms_cubic-bezier(0.23,1,0.32,1),box-shadow_220ms_ease-out]',
        'hover:[@media(hover:hover)_and_(pointer:fine)]:shadow-md',
        'active:scale-[0.98]',
        config.border,
        config.bg,
        pulse && 'animate-pulse-border',
        href && 'cursor-pointer'
      )}
      onClick={href ? () => navigate(href) : undefined}
      style={pulse ? {
        boxShadow: `0 0 20px 0 var(--tw-shadow-color)`,
        '--tw-shadow-color': config.pulse.replace('shadow-', '').replace('/20', '')
      } as React.CSSProperties : undefined}
    >
      {/* Animated gradient overlay for active states */}
      {pulse && (
        <div
          className={cn(
            'absolute inset-0 opacity-10 animate-shimmer',
            status === 'critical' && 'bg-gradient-to-r from-transparent via-red-500 to-transparent',
            status === 'warning' && 'bg-gradient-to-r from-transparent via-amber-500 to-transparent',
            status === 'success' && 'bg-gradient-to-r from-transparent via-emerald-500 to-transparent',
            status === 'voice' && 'bg-gradient-to-r from-transparent via-violet-500 to-transparent',
            status === 'normal' && 'bg-gradient-to-r from-transparent via-brand-primary to-transparent'
          )}
        />
      )}

      <CardContent className="p-4 relative z-10">
        <div className="flex items-center gap-4">
          {/* Icon with status ring */}
          <div className={cn(
            'relative flex items-center justify-center h-12 w-12 rounded-full',
            config.bg,
            'ring-2',
            config.border.replace('border-', 'ring-')
          )}>
            <Icon className={cn('h-6 w-6', config.icon)} />
            {/* Pulsing dot indicator */}
            {pulse && (
              <span className={cn(
                'absolute -top-1 -right-1 h-3 w-3 rounded-full',
                status === 'critical' && 'bg-red-500',
                status === 'warning' && 'bg-amber-500',
                status === 'success' && 'bg-emerald-500',
                status === 'voice' && 'bg-violet-500',
                status === 'normal' && 'bg-brand-primary'
              )}>
                <span className={cn(
                  'absolute inset-0 rounded-full animate-ping',
                  status === 'critical' && 'bg-red-500',
                  status === 'warning' && 'bg-amber-500',
                  status === 'success' && 'bg-emerald-500',
                  status === 'voice' && 'bg-violet-500',
                  status === 'normal' && 'bg-brand-primary'
                )} />
              </span>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-muted-foreground truncate">
              {title}
            </p>
            <p className={cn(
              // Dropped extrabold in favor of semibold + tabular-nums.
              // Hierarchy reads better when hero values aren't shouting.
              'text-3xl font-semibold tracking-tight tabular-nums',
              config.icon
            )}>
              {typeof value === 'number' ? animatedValue : value}
            </p>
            {subtitle && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {subtitle}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
